import {
	type FunctionComponent,
	type PropsWithChildren,
	type ReactElement,
	Children,
	cloneElement,
	useEffect,
	useRef,
	useState
} from 'react';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { ClickableElement } from '../../buttons';
import { Link } from '../link/link.component';

export type TabProps = {
	name: string;
	isActive?: boolean;
	isDisabled?: boolean;
	url?: string;
	onClick?: Function;
	tabClasses?: string;
	tabActiveClasses?: string;
	icon?: ReactElement;
	automationHook?: string;
	renderActiveTabOnly?: boolean;
};

export const Tab: FunctionComponent<PropsWithChildren<TabProps>> = ({ isActive = false, renderActiveTabOnly = false, name, children }) => {
	const tabRendered = useRef(false);

	const renderTab = renderActiveTabOnly ? isActive : isActive || tabRendered.current;

	if (isActive && !tabRendered.current) {
		tabRendered.current = true;
	}

	return renderTab ? (
		<div role="tabpanel" data-testid={`${name.toLowerCase()}-tab-content`} className={isActive ? 'db' : 'dn'}>
			{children}
		</div>
	) : null;
};

export type TabsProps = {
	startingTabName: string;
	tabControlClasses?: string;
	tabListClasses?: string;
	children: (ReactElement | false)[];
	tabStyle?: 'DEFAULT' | 'RADIO' | 'NONE';
	onActiveTabChanged?: (tabName: string) => void;
	renderActiveTabOnly?: boolean;
};

export const Tabs: FunctionComponent<TabsProps> = ({
	startingTabName,
	tabControlClasses,
	tabStyle = 'DEFAULT',
	tabListClasses = 'justify-between justify-start-ns',
	onActiveTabChanged,
	renderActiveTabOnly,
	children
}) => {
	const [activeTabName, setActiveTabName] = useState(startingTabName);
	function registerClick(tabName) {
		return function handleClick() {
			setActiveTabName(tabName);
		};
	}

	// Make state respond to prop change
	useEffect(() => {
		setActiveTabName(startingTabName);
	}, [startingTabName]);

	function getTabInfo(tab: ReactElement) {
		const tabName = tab.props.name;
		const { isDisabled, url, onClick, tabClasses, tabActiveClasses, icon, automationHook } = tab.props;
		const isActive = Boolean(tabName === activeTabName);
		return {
			tabName,
			isActive,
			isDisabled,
			url,
			onClick,
			tabClasses,
			tabActiveClasses,
			icon,
			automationHook
		};
	}
	const tabList = Children.map(children, (child, index) => {
		// Child can be falsy when tab conditionally rendered
		if (!child) {
			return null;
		}
		const { tabName, isActive, isDisabled, url, onClick, tabClasses, tabActiveClasses, icon, automationHook } = getTabInfo(child);
		const tabStyleClasses = isDisabled
			? 'theme-grey-light disableClickEvents'
			: isActive && tabStyle === 'DEFAULT'
			? 'theme-primary'
			: 'theme-grey-darker';

		const activateTab = () => {
			if (onClick) {
				onClick();
			}
			setActiveTabName(tabName);
			if (onActiveTabChanged) {
				onActiveTabChanged(tabName);
			}
		};

		return (
			<>
				{url ? (
					<ClickableElement
						ariaLabel={tabName}
						className={tabClasses || 'pb3 b'}
						key={index}
						tabIndex={0}
						aria-selected={isActive}
						onClick={onClick}
						onKeyDown={handleKeys(['Enter', ' '], onClick)}>
						<Link
							url={url}
							className={`lh-title`}
							textAlign={'tc'}
							tabIndex={0}
							underlineHover={true}
							color={isActive ? 'primary' : 'grey-darker'}
							automationHook={automationHook}>
							<>{icon ? icon : tabName}</>
						</Link>
					</ClickableElement>
				) : (
					<ClickableElement
						ariaLabel={`${tabName} tab`}
						className={`${
							(isActive && tabActiveClasses ? tabActiveClasses : tabClasses) || 'pb3 b'
						} ${tabStyleClasses} pointer ${tabStyle === 'RADIO' ? 'flex items-center' : ''}`}
						key={index}
						tabIndex={0}
						aria-selected={isActive}
						onClick={activateTab}
						onKeyDown={handleKeys(['Enter', ' '], activateTab)}
						data-automation={generateDataSelector('tab', automationHook)}>
						{tabStyle === 'RADIO' && (
							<input
								type="radio"
								aria-label={tabName}
								checked={isActive}
								className="ma0 mr2 pointer v-mid"
								onChange={registerClick(tabName)}
							/>
						)}
						{icon ? icon : tabName}
					</ClickableElement>
				)}
			</>
		);
	});

	const tabPanels = Children.map(children, (child) => {
		// Child can be falsy when tab conditionally rendered
		if (!child) {
			return null;
		}

		const { isActive, url } = getTabInfo(child);
		return url ? null : cloneElement(child, { isActive, renderActiveTabOnly });
	});

	return (
		<section>
			<div className={`bb ${tabControlClasses || ''}`}>
				<div role="presentation" className={`flex ${tabListClasses} ma0`}>
					{tabList}
				</div>
			</div>
			{tabPanels}
		</section>
	);
};
